import {
  queryKeys,
  useMe,
  useVidarioClient,
  type AuthClient,
  type AuthSession,
  type SignInResult,
} from "@vidario/api-client";
import { type User } from "@vidario/domain";
import { useQueryClient } from "@tanstack/react-query";

import { anonymView } from "../platform/anonym-view";
import { onSessionExpired } from "../platform/session-expiry";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
  type ReactElement,
} from "react";

export interface AuthContextValue {
  /** `true` until the initial session fetch resolves. Guards against
   * flashing `/login` during the first paint. */
  initializing: boolean;
  /** The auth-level session ({ userId, email }). `null` when signed out. */
  session: AuthSession | null;
  /** The app-level User profile from `/me`. `null` when signed out or
   * still loading the profile. */
  user: User | null;
  /** `/me` failed for a reason other than an expired session. */
  userError: boolean;
  retryUser: () => void;
  /** The session ended on its own (not a sign-out); login says so (#23). */
  sessionExpired: boolean;
  /** Step 1 of sign-in. Resolves to a ready session or a 2FA challenge —
   * the caller routes to the TOTP step on `two_fa_required`. */
  signIn: (email: string, password: string) => Promise<SignInResult>;
  /** Step 2 — verify the TOTP code from a 2FA challenge. */
  verifyTwoFa: (
    ticket: string,
    code: string,
    options?: { rememberDevice?: boolean },
  ) => Promise<void>;
  signOut: () => Promise<void>;
  resetPasswordForEmail: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps extends PropsWithChildren {
  client: AuthClient;
}

export function AuthProvider({ children, client }: AuthProviderProps): ReactElement {
  const qc = useQueryClient();
  const api = useVidarioClient();
  const [session, setSession] = useState<AuthSession | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const sessionRef = useRef<AuthSession | null>(null);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    let cancelled = false;
    void client
      .getSession()
      .catch(() => null)
      .then((s) => {
        if (cancelled) return;
        setSession(s);
        setInitializing(false);
      });
    const unsubscribe = client.onAuthStateChange((next) => {
      setSession(next);
      if (next) setSessionExpired(false);
      qc.invalidateQueries({ queryKey: queryKeys.me() });
    });
    // Only an active session can expire: the checks before login and the
    // login page's own requests must not loop back here.
    const unsubscribeExpiry = onSessionExpired(() => {
      if (!sessionRef.current) return;
      sessionRef.current = null;
      // A new login starts without the Anonym-Ansicht (#140).
      anonymView.clear();
      setSessionExpired(true);
      setSession(null);
      qc.clear();
      void client.signOut().catch(() => undefined);
    });
    return () => {
      cancelled = true;
      unsubscribe();
      unsubscribeExpiry();
    };
  }, [client, qc]);

  const me = useMe({ enabled: Boolean(session) });
  const user = session ? (me.data ?? null) : null;
  const userError = Boolean(session) && me.isError && !me.data;
  const refetchMe = me.refetch;

  const value = useMemo<AuthContextValue>(
    () => ({
      initializing,
      session,
      user,
      userError,
      retryUser: () => void refetchMe(),
      sessionExpired,
      async signIn(email, password) {
        anonymView.clear();
        return client.signInWithPassword(email, password);
      },
      async verifyTwoFa(ticket, code, options) {
        await client.verifyTwoFa(ticket, code, options);
      },
      async signOut() {
        // Logging out switches the Anonym-Ansicht off: logged like any switch (#140).
        if (anonymView.get()) await api.logHideModeSwitch(false).catch(() => undefined);
        anonymView.clear();
        await client.signOut();
        qc.clear();
      },
      async resetPasswordForEmail(email) {
        await client.resetPasswordForEmail(email);
      },
      async updatePassword(newPassword) {
        await client.updatePassword(newPassword);
      },
    }),
    [initializing, session, user, userError, refetchMe, sessionExpired, client, qc, api],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>.");
  return ctx;
}
